import React, { useState, useEffect, useCallback } from 'react';
import { Flame, CheckCircle, Calendar, Loader2, Gift } from 'lucide-react';
import { useAuth } from '../../contexts/AuthContext';
import { apiClient } from '../../api/client';
import DailyCheckInModal from '../modals/DailyCheckInModal';
import { devLog } from '../../utils/devLogger';
import useCreditsLabel from '../../hooks/useCreditsLabel';

const CurrentStreakCard = () => {
  const { user, refreshUser } = useAuth();
  const { label: creditsLabel } = useCreditsLabel();
  const [streakInfo, setStreakInfo] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showCheckInModal, setShowCheckInModal] = useState(false);
  const [checkingIn, setCheckingIn] = useState(false);

  // useCallback Guard: wraps a mount-fetch. Only `user?.current_streak`
  // is closed over (used in the offline fallback if the API fails). Keying
  // on it means: if the auth context updates the streak while the card is
  // mounted (e.g., another tab logs a check-in), we refetch and stay in
  // sync. All other reads (React setters, imported apiClient) are stable.
  const fetchStreakInfo = useCallback(async () => {
    setLoading(true);
    try {
      const data = await apiClient.getStreakInfo();
      setStreakInfo(data);
    } catch (err) {
      console.error('Failed to fetch streak info:', err);
      // Fallback to user data
      setStreakInfo({
        current_streak: user?.current_streak || 0,
        checked_in_today: false,
        next_milestone: 7,
        milestone_reward: 25
      });
    } finally {
      setLoading(false);
    }
  }, [user?.current_streak]);

  useEffect(() => {
    fetchStreakInfo();
  }, [fetchStreakInfo]);

  const handleCheckIn = () => {
    setShowCheckInModal(true);
  };

  const handleCheckInComplete = async (checkinData) => {
    setCheckingIn(true);
    try {
      const result = await apiClient.dailyCheckin(checkinData || {});

      // Update local state
      setStreakInfo(prev => ({
        ...prev,
        current_streak: result.streak,
        checked_in_today: true
      }));

      // Refresh user data to update hearts
      if (refreshUser) {
        await refreshUser();
      }

      setShowCheckInModal(false);

      // Show success message (could use toast)
      devLog(`Check-in complete! ${result.hearts_earned} ${creditsLabel.toLowerCase()} earned.`);

    } catch (err) {
      console.error('Check-in failed:', err);
    } finally {
      setCheckingIn(false);
    }
  };

  if (loading) {
    return (
      <div className="card" data-testid="current-streak-card">
        <div className="card-header">
          <h2 className="card-title">Current Streak</h2>
        </div>
        <div className="flex items-center justify-center py-8">
          <Loader2 className="w-6 h-6 animate-spin text-gray-400" />
        </div>
      </div>
    );
  }

  const currentStreak = streakInfo?.current_streak || 0;
  const nextMilestone = streakInfo?.next_milestone || 7;
  const milestoneReward = streakInfo?.milestone_reward || 25;
  const hasCheckedIn = streakInfo?.checked_in_today || false;
  const progressPercentage = (currentStreak / nextMilestone) * 100;

  return (
    <>
      <div className="card" data-testid="current-streak-card">
        <div className="card-header">
          <h2 className="card-title">Current Streak</h2>
          <button
            onClick={() => window.location.href = '/wellness'}
            className="text-xs text-blue-600 hover:text-blue-700 font-medium"
          >
            View History
          </button>
        </div>

        {/* Streak Display */}
        <div className="text-center py-6">
          <div className={`inline-flex items-center justify-center w-20 h-20 rounded-full mb-4 ${
            currentStreak > 0
              ? 'bg-gradient-to-br from-orange-400 to-red-500 animate-pulse'
              : 'bg-gray-200'
          }`}>
            <Flame size={40} className={currentStreak > 0 ? 'text-white' : 'text-gray-400'} />
          </div>
          <h3 className="text-4xl font-bold text-gray-900 mb-2">
            {currentStreak}
            <span className="text-lg text-gray-600 ml-2">days</span>
          </h3>
          <p className="text-sm text-gray-600">
            {currentStreak > 0 ? 'Keep your streak alive!' : 'Start your streak today!'}
          </p>
        </div>

        {/* Progress to Next Milestone */}
        <div className="mb-4">
          <div className="flex items-center justify-between mb-2">
            <span className="text-sm font-medium text-gray-700">Progress to {nextMilestone} days</span>
            <span className="text-sm font-semibold text-orange-600">
              {currentStreak}/{nextMilestone}
            </span>
          </div>
          <div className="w-full bg-gray-200 rounded-full h-3 overflow-hidden">
            <div
              className="bg-gradient-to-r from-orange-400 to-red-500 h-3 rounded-full transition-all duration-500"
              style={{ width: `${Math.min(progressPercentage, 100)}%` }}
            />
          </div>
          <div className="flex items-center gap-1 mt-2 text-xs text-gray-600">
            <Gift size={14} className="text-orange-500" />
            <span>Earn {milestoneReward} hearts at {nextMilestone} days!</span>
          </div>
        </div>

        {/* Check-In Button */}
        <button
          onClick={handleCheckIn}
          disabled={hasCheckedIn || checkingIn}
          className={`w-full py-3 rounded-lg font-semibold text-white transition-all flex items-center justify-center gap-2 ${
            hasCheckedIn
              ? 'bg-green-500 cursor-default'
              : 'bg-gradient-to-r from-orange-500 to-red-500 hover:shadow-lg hover:-translate-y-0.5'
          }`}
          data-testid="daily-checkin-button"
        >
          {checkingIn ? (
            <Loader2 size={18} className="animate-spin" />
          ) : hasCheckedIn ? (
            <>
              <CheckCircle size={18} />
              Check-In Complete!
            </>
          ) : (
            <>
              <Calendar size={18} />
              Complete Daily Check-In
            </>
          )}
        </button>

        {!hasCheckedIn && (
          <p className="text-xs text-center text-gray-600 mt-2">
            Keep your momentum going — check in with today&apos;s practice!
          </p>
        )}
      </div>

      {/* Daily Check-In Modal */}
      <DailyCheckInModal
        isOpen={showCheckInModal}
        onClose={() => setShowCheckInModal(false)}
        onComplete={handleCheckInComplete}
      />
    </>
  );
};

export default CurrentStreakCard;
